import { useId } from 'react';
import { useTheme } from '../theme/ThemeProvider.js';
import { APPEARANCES, type Appearance } from '../theme/storage.js';
import { CATEGORY_COUNT, DENSITIES, type LayoutFeatures } from '../themes/types.js';
import { cx } from './cx.js';

const APPEARANCE_LABEL: Record<Appearance, string> = {
  system: 'Match this device',
  light: 'Light',
  dark: 'Dark',
};

/**
 * Lists every registered theme with a live preview (each preview renders inside its
 * own `data-theme`, so it shows that theme's real tokens). Choosing one applies at once.
 */
export function ThemePicker({ legend = 'Theme' }: { legend?: string }) {
  const { themes, theme, setTheme, appearance, setAppearance } = useTheme();
  const name = useId();
  return (
    <div className="qk-theme-picker">
      <fieldset className="qk-fieldset">
        <legend className="qk-fieldset__legend">{legend}</legend>
        <div className="qk-theme-picker__options">
          {themes.map((t) => (
            <label
              key={t.id}
              className={cx('qk-theme-option', t.id === theme.id && 'qk-theme-option--selected')}
            >
              <input
                type="radio"
                name={name}
                value={t.id}
                checked={t.id === theme.id}
                onChange={() => setTheme(t.id)}
                className="qk-theme-option__input"
              />
              <span className="qk-theme-preview" data-theme={t.id} aria-hidden="true">
                <span className="qk-theme-preview__bar" />
                <span className="qk-theme-preview__tiles">
                  {Array.from({ length: 4 }, (_, i) => (
                    <span
                      key={i}
                      className="qk-theme-preview__tile"
                      style={{
                        background: `var(--color-category-${(i % CATEGORY_COUNT) + 1})`,
                      }}
                    />
                  ))}
                </span>
                <span className="qk-theme-preview__button" />
              </span>
              <span className="qk-theme-option__text">
                <span className="qk-theme-option__name">{t.name}</span>
                <span className="qk-theme-option__description">{t.description}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>
      {theme.alternate ? (
        <fieldset className="qk-fieldset">
          <legend className="qk-fieldset__legend">Light or dark</legend>
          <div className="qk-segmented">
            {APPEARANCES.map((a) => (
              <label key={a} className="qk-segmented__option">
                <input
                  type="radio"
                  name={`${name}-appearance`}
                  value={a}
                  checked={appearance === a}
                  onChange={() => setAppearance(a)}
                />
                <span>{APPEARANCE_LABEL[a]}</span>
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}
    </div>
  );
}

const FEATURE_LABELS: Record<Exclude<keyof LayoutFeatures, 'density'>, [string, string]> = {
  itemCodeEntry: ['Item codes', 'Type a short code and press Enter to add an item'],
  tableStrip: ['Open tables strip', 'Show open tables across the top bar'],
  categoryColours: ['Category colours', 'Tint item tiles by menu category'],
  keyboardShortcuts: ['Keyboard shortcuts', 'F-keys on tender buttons'],
};

/** Layout features on top of the theme's defaults (they work in every theme). */
export function FeatureToggles() {
  const { features, setFeature, resetFeatures } = useTheme();
  const name = useId();
  return (
    <fieldset className="qk-fieldset">
      <legend className="qk-fieldset__legend">Layout</legend>
      <div className="qk-toggles">
        {(Object.keys(FEATURE_LABELS) as (keyof typeof FEATURE_LABELS)[]).map((key) => (
          <label key={key} className="qk-toggle">
            <input
              type="checkbox"
              checked={features[key]}
              onChange={(e) => setFeature(key, e.target.checked)}
            />
            <span className="qk-toggle__text">
              <span className="qk-toggle__name">{FEATURE_LABELS[key][0]}</span>
              <span className="qk-toggle__hint">{FEATURE_LABELS[key][1]}</span>
            </span>
          </label>
        ))}
      </div>
      <div className="qk-segmented" role="radiogroup" aria-label="Density">
        {DENSITIES.map((d) => (
          <label key={d} className="qk-segmented__option">
            <input
              type="radio"
              name={`${name}-density`}
              value={d}
              checked={features.density === d}
              onChange={() => setFeature('density', d)}
            />
            <span>{d === 'comfortable' ? 'Comfortable' : 'Compact'}</span>
          </label>
        ))}
      </div>
      <button type="button" className="qk-btn qk-btn--ghost qk-btn--md" onClick={resetFeatures}>
        <span className="qk-btn__label">Use the theme&apos;s defaults</span>
      </button>
    </fieldset>
  );
}
